<template>
  <p-form-element v-bind="fallthroughTags" show-messages :errors="innerErrors" :messages="innerMessages">
    <div :class="pTextclassList">
      <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
      <label v-if="!solo" :for="'text-field-' + _uid" class="text-field-label">
        {{ innerLabel }}
      </label>
      <div :class="inputWrapperClassList">
        <div class="context-slot">
          <slot name="context">
            <p-icon
              v-if="computedContextIcon"
              class="ml-1"
              :color="disabled? 'disabled': 'readonly'"
              :disabled="disabled || readonly"
            >
              {{ computedContextIcon }}
            </p-icon>
          </slot>
        </div>
        <p-conditional-wrapper :show="_.component.hasSlot(this, 'input')">
          <div
            ref="input"
            class="input"
            v-on="{
              ...$listeners,
              input: onInput,
              blur: onBlur,
              focus: onFocus,
              click: onClick
            }"
          >
            <slot name="input">
              <input
                :id="'text-field-' + _uid"
                ref="input"
                class="input"
                :role="computedRole"
                :type="innerType"
                :readonly="readonly || disabled"
                :value="value"
                :autocomplete="autocompleteComputed"
                :aria-errormessage="innerErrors"
                :min="min"
                :max="max"
                :step="step"
                :placeholder="placeholderComputed"
                :inputmode="inputmode"
                :pattern="pattern"
                :required="innerRequired ? true : undefined"
                :title="solo ? placeholder : undefined"
                v-bind="fallthroughAriaTags"
                v-on="{
                  ...$listeners,
                  input: onInput,
                  blur: onBlur,
                  focus: onFocus,
                  click: onClick
                }"
              >
            </slot>
          </div>
        </p-conditional-wrapper>
        <div class="actions-slot">
          <slot name="clear">
            <div v-if="canClear" class="clear-action">
              <p-button :aria-label="$t('core.app.clearEntry', label)" dense tertiary @click="onClearClick">
                <p-icon>
                  clear
                </p-icon>
              </p-button>
            </div>
          </slot>
          <slot name="actions">
            <p-icon
              v-if="actionIcon"
              class="ml-1 action-icon"
              :color="disabled? 'disabled': undefined"
              :disabled="canPerformActions"
              @click="onActionIconClick"
            >
              {{ actionIcon }}
            </p-icon>
          </slot>
        </div>
      </div>
      <div v-if="hasMetaSlot" class="meta-slot">
        <slot name="meta">
        </slot>
      </div>
    </div>
  </p-form-element>
</template>

<script lang="ts">
  import interactable from '../../mixins/interactable/interactable';
  import colorable from '../../mixins/colorable/colorable';
  import clearable from '../../mixins/clearable/clearable';
  import loadable from '../../mixins/loadable/loadable';
  import validatable from '../../mixins/validatable/validatable';
  import mixins from '../../mixins';
  import _ from '../../utils';

  export default mixins(interactable, colorable, clearable, loadable, validatable).extend({
    inheritAttrs: false,
    props: {
      pattern: { default: undefined, type: String },
      inputmode: {
        default: undefined,
        type: String,
        validator: (value) => {
          return value === 'text'
            || value === 'search'
            || value === 'tel'
            || value === 'url'
            || value === 'email'
            || value === 'numeric'
            || value === 'decimal'
            || value === 'none'
            || value === undefined;
        },
      },
      step: { default: undefined, type: [String, Number] },
      min: { default: undefined, type: [String, Number] },
      max: { default: undefined, type: [String, Number] },
      role: { default: undefined, type: String },
      type: {
        default: 'text',
        type: String,
        validator: (value) => {
          return value === 'color'
            || value === 'date'
            || value === 'datetime-local'
            || value === 'email'
            || value === 'file'
            || value === 'image'
            || value === 'month'
            || value === 'number'
            || value === 'password'
            || value === 'radio'
            || value === 'range'
            || value === 'reset'
            || value === 'search'
            || value === 'submit'
            || value === 'tel'
            || value === 'text'
            || value === 'time'
            || value === 'url'
            || value === 'week'
            || value === undefined;
        },
      },
      value: { default: '', type: [String, Number, Boolean] },
      label: { default: '', type: String },
      color: { default: undefined, type: String },
      contextIcon: { default: undefined, type: String },
      actionIcon: { default: undefined, type: String },
      clearableOnReadonly: { default: false, type: Boolean },
      clearableWithoutValue: { default: false, type: Boolean },
      autocomplete: { default: false, type: [Boolean, String] },
      solo: { default: false, type: Boolean },
      autofocus: { default: false, type: Boolean },
      placeholder: { default: undefined, type: String },
    },
    data: () => ({
      isFocused: false,
    }),
    mounted() {
      // To guarantee autofocus it needs to be done programmatically
      // The HTML5 attribute is ignored if the input is mounted slightly delayed and is unreliable
      if (this.autofocus) {
        const el = this.$refs.input as HTMLInputElement;
        el.focus();
      }
    },
    computed: {
      fallthroughTags() {
        return {
          class: this.$attrs.class,
          style: this.$attrs.style,
        };
      },
      fallthroughAriaTags() {
        const ariaTags = {} as Record<string, any>;
        Object.keys(this.$attrs).forEach((attr) => {
          if (attr.toLowerCase().includes('aria-')) {
            ariaTags[attr] = this.$attrs[attr];
          }
        });
        return ariaTags;
      },
      isSearch(): boolean {
        return this.type?.trim().toLowerCase() === 'search' || this.contextIcon?.trim().toLowerCase() === 'search';
      },
      computedContextIcon(): string | undefined {
        if (!this.contextIcon && this.isSearch) {
          return 'search';
        }
        return this.contextIcon;
      },
      computedRole(): string {
        return this.role ?? (this.isSearch ? 'searchbox' : 'textbox');
      },
      placeholderComputed() {
        return this.solo ? this.innerLabel : this.placeholder;
      },
      autocompleteComputed() {
        if (typeof (this.autocomplete) === 'string') {
          return this.autocomplete;
        }
        return this.autocomplete ? 'on' : 'off';
      },
      canPerformActions() {
        return !this.readonly && !this.disabled;
      },
      canClear(): boolean {
        return this.clearable && !this.disabled && (!this.readonly || this.clearableOnReadonly) && (this.clearableWithoutValue || this.hasValue);
      },
      pTextclassList(): any {
        return {
          'p-text-field': true,
          solo: this.solo,
          required: this.innerRequired,
          active: (this.hasValue || this.isFocused || this.placeholder),
          focused: this.isFocused,
          disabled: this.disabled,
          loading: this.loading,
          invalid: (this.innerErrors ?? []).length > 0,
          'has-context-slot': this.hasContextSlot,
          'has-actions-slot': this.hasActionsSlot,
          'is-clearable': this.clearable,
        };
      },
      hasErrors() {
        return this.innerErrors && this.innerErrors.length > 0;
      },
      hasMessages() {
        return this.innerMessages && this.innerMessages.length > 0;
      },
      hasMetaSlot() {
        return _.component.hasSlot(this, 'meta');
      },
      hasContextSlot(): boolean {
        return !!this.computedContextIcon || _.component.hasSlot(this, 'context');
      },
      hasActionsSlot() {
        return this.actionIcon || _.component.hasSlot(this, 'actions');
      },
      inputWrapperClassList(): any {
        return {
          'p-text-field-input-wrapper': true,
          [`border-color-${this.color}`]: !this.isFocused && this.color,
        };
      },
      hasValue() : boolean {
        const value = (this.value ?? '').toString();
        return value !== '';
      },
    },
    methods: {
      onActionIconClick(value: MouseEvent) {
        if (this.$listeners['action-icon-click']) {
          this.$emit('action-icon-click', value);
        } else {
          (this.$refs.input as HTMLElement).focus();
          this.onClick(value);
        }
      },
      onClearClick() {
        this.$emit('input', undefined);
        this.$emit('clear');
      },
      onClick(value: MouseEvent) {
        this.$emit('click', value);
      },
      onFocus(value: FocusEvent) {
        this.$emit('focus', value);
        this.isFocused = true && !this.disabled;
      },
      onBlur(value: FocusEvent) {
        this.$emit('blur', value);
        this.isFocused = false;
      },
      onInput(event: InputEvent) {
        const target = event.currentTarget as HTMLInputElement;
        const { value } = target;
        if (typeof value === 'string') {
          if (value.trim() === '') {
            this.$emit('input', undefined);
            return;
          }
        }
        this.$emit('input', target.value);
      },
    },

  });
</script>
